import { retry } from 'rxjs/operators';
import { SinglePackage } from 'utils/api/models/numbers/singlePackage';
import { SingleCustomerProduct } from 'utils/api/models/products/singleCustomerProduct';
import { SingleSubscriberProduct } from 'utils/api/models/subscriberProducts/singleSubscriberProduct';
import { getProductId } from 'views/Numbers/Details/Services/helpers/getProductId';

type CompareFunction = (singleUsagePackage: SingleCustomerProduct[], singlePackage: SinglePackage) => boolean;
export const SERVICE_STATUS = {
	ACTIVE: 'Active',
	INACTIVE: 'Inactive',
	PENDING: 'PENDING',
	PENDING_ACTIVATION: 'Pending Activation',
	PENDING_TERMINATION: 'Pending Termination',
};

function _getUsageIDKey() {
	return 'productId';
}

function _compare(
	customerProducts: (SingleCustomerProduct | SingleSubscriberProduct)[],
	singlePackage: SinglePackage,
	compareFunction: CompareFunction
) {
	if (customerProducts && Array.isArray(customerProducts) && singlePackage) {
		return !!customerProducts.filter((singleProduct: any) => {
			return compareFunction(singleProduct, singlePackage);
		}).length;
	} else {
		return false;
	}
}

export function isEnabled(products: (SingleCustomerProduct | SingleSubscriberProduct)[], singlePackage: SinglePackage) {
	const compareEnabled: CompareFunction = (singleProduct: any, singlePackage) => {
		return singleProduct[_getUsageIDKey()] === singlePackage.partNumberRaw;
	};

	return _compare(products, singlePackage, compareEnabled);
}
export function getStatus(products: SingleSubscriberProduct[], singlePackage: SinglePackage) {
	const getProduct = products?.find(
		(product: SingleSubscriberProduct) => product.productId && product.productId === singlePackage.partNumberRaw
	);
	if (getProduct) {
		switch (getProduct.status) {
			case SERVICE_STATUS.ACTIVE:
				return 'ACTIVE';
			case SERVICE_STATUS.INACTIVE:
				return 'INACTIVE';
			case SERVICE_STATUS.PENDING:
				return 'PENDING';
			case SERVICE_STATUS.PENDING_ACTIVATION:
				return 'PENDING_ACTIVATION';
			case SERVICE_STATUS.PENDING_TERMINATION:
				return 'PENDING_TERMINATION';
			default:
				return 'INACTIVE';
		}
	}

	return 'INACTIVE';
}

export function isExcluded(
	products: (SingleCustomerProduct | SingleSubscriberProduct)[],
	singlePackage: SinglePackage
) {
	const compareExcluded: CompareFunction = (singleProduct: any, singlePackage) => {
		const excludedBy = singlePackage.excludedBy;
		if (Array.isArray(excludedBy)) {
			const excludedByIds = excludedBy.map((singleExcludedById) => getProductId(singleExcludedById));
			return excludedByIds.includes(singleProduct[_getUsageIDKey()]);
		} else {
			return false;
		}
	};

	return _compare(products, singlePackage, compareExcluded);
}

export function hasRequiredProducts(
	products: (SingleCustomerProduct | SingleSubscriberProduct)[],
	singlePackage: SinglePackage
) {
	const compareRequired: CompareFunction = (singleProduct: any, singlePackage) => {
		const requiredProducts = singlePackage.requiredProducts;
		if (Array.isArray(requiredProducts) && !!requiredProducts.length) {
			const requiredProductsIds = requiredProducts.map((singleRequiredProduct) =>
				getProductId(singleRequiredProduct)
			);
			return requiredProductsIds.includes(singleProduct[_getUsageIDKey()]);
		} else {
			return true;
		}
	};

	return _compare(products, singlePackage, compareRequired);
}

export function isActivationAllowed(
	products: (SingleCustomerProduct | SingleSubscriberProduct)[],
	singlePackage: SinglePackage,
	noRequiredProducts?: boolean
): boolean {
	const { selfActivate, multiactivate, selfDeactivate } = singlePackage;
	const enabled = isEnabled(products, singlePackage);
	const excluded = isExcluded(products, singlePackage);
	const requiredProducts = noRequiredProducts ? true : hasRequiredProducts(products, singlePackage);
	/*
	 * show packages where:
	 *
	 * - !enabled, selfActivation
	 * - enabled, selfDeactivation, !multiActivation
	 * - enabled, !selfDeactivation, multiActivation
	 *
	 */
	return selfActivate && (!enabled || multiactivate || selfDeactivate) && !excluded && requiredProducts;
}

export function isPackageAllowed(
	products: (SingleCustomerProduct | SingleSubscriberProduct)[],
	singlePackage: SinglePackage,
	noRequiredProducts?: boolean
): boolean {
	const { selfActivate, multiactivate, selfDeactivate } = singlePackage;
	const enabled = isEnabled(products, singlePackage);
	const excluded = isExcluded(products, singlePackage);
	const requiredProducts = noRequiredProducts ? true : hasRequiredProducts(products, singlePackage);
	return (selfActivate && (!enabled || multiactivate || selfDeactivate) && !excluded && requiredProducts) || enabled;
}
